import { GetInitialDataCtx, WidgetProps } from "../../contracts/widget"
import { spacesWidgetConfig, SpacesWidgetConfig } from "./config"
import { WidgetProvider, useWidget } from "../../providers/widget"
import { useFetchSpaces, isPlaceholderSpace, type PropertySpace } from "./data"
import "./spaces.css"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Stylesheet from "../../components/Stylesheet"
import WebFont from "../../components/WebFont"
import WidgetSection from "../../components/WidgetSection"
import WidgetDataError from "../../components/WidgetDataError"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"
import { usePropertyCustomFields } from "../../common/property-custom-fields"
import { resolveBedroomMedia, type BedroomMedia } from "../../common/bedroom-showcase"
import { assignCaptionPhotos } from "../../common/bedroom-captions"
import { galleryFilterFor, useSharedGalleryPhotos } from "./gallery-photos"
import { assignRoomPhotos } from "./room-photos"
import { requestGalleryOpenAt } from "../gallery/open-at"
import { getProxiedImageUrlOnce } from "../../common/image-proxy"
import { hostFontSizeCss } from "../../common/host-scope"
import { useFocusTrap, useModalPresence } from "../gallery/hooks"

export const getInitialData = async ({ }: GetInitialDataCtx<SpacesWidgetConfig>) => {
    return {}
}

/**
 * Bedroom index for a space (dashboard#1420 PR B): the platform's own
 * `number` field when present (real data, unlike the toolkit's name-digit
 * extraction fallback — see common/bedroom-showcase.ts), else this room's
 * 1-based position among BEDROOM-type spaces only (so a property with
 * Bedroom + Bathroom + Bedroom still maps the second bedroom to index 2,
 * not 3).
 */
function bedroomIndexes(spaces: PropertySpace[]): Map<number, number> {
    const indexes = new Map<number, number>()
    let position = 0
    for (const space of spaces) {
        if ((space.type ?? '').toLowerCase() !== 'bedroom') continue
        position += 1
        indexes.set(space.id, typeof space.number === 'number' && space.number > 0 ? space.number : position)
    }
    return indexes
}

/**
 * A room's photos with the caption each one carries ('' when unknown) and,
 * when the PMS room map matched this space, the PMS room's uid (what a
 * tap = tour hands the gallery so it can open at the room even for a field
 * photo the gallery does not hold).
 */
type RoomMedia = BedroomMedia & { captions: string[]; roomUid?: string }

type SlideTransition = 'fade' | 'slide' | 'none'

/**
 * The photo slot of a room card (dashboard#1707, toolkit bedroom-showcase
 * 3.244.0 slideshow).
 *
 * One image renders the single thumbnail button exactly as before. Several
 * render a slideshow: `role="group"` + `aria-roledescription="slideshow"`,
 * prev/next buttons, one dot per photo and a live "n / total" counter, arrow
 * keys while focus is inside, and the active slide opens the shared lightbox
 * at its own index.
 *
 * No-JS / first paint: the server emits the group with the FIRST slide only and
 * no controls (they would do nothing without script). The controls, and the
 * remaining slides, mount after hydration; a slide is mounted once it is the
 * active one or its neighbour, so photos load as a guest steps toward them
 * instead of all up front. Nothing advances on its own.
 */
function RoomPhotos({ name, media, transition, onOpen }: {
    name: string
    media: RoomMedia
    transition: SlideTransition
    onOpen: (index: number, trigger: HTMLElement) => void
}) {
    const { images, captions } = media
    const total = images.length
    const [current, setCurrent] = useState(0)
    const [interactive, setInteractive] = useState(false)
    const [seen, setSeen] = useState<number[]>([0])
    useEffect(() => { setInteractive(true) }, [])

    const go = useCallback((target: number) => {
        const next = ((target % total) + total) % total
        setCurrent(next)
        setSeen((prev) => Array.from(new Set([...prev, next, (next + 1) % total, (next - 1 + total) % total])))
    }, [total])
    // Neighbours of slide 0 load once the controls exist.
    useEffect(() => { if (interactive && total > 1) go(0) }, [interactive, total, go])

    if (total === 1) {
        return (
            <button
                type="button"
                className="homerunner-spaces-photo-trigger"
                onClick={(e) => onOpen(0, e.currentTarget)}
                aria-label={`View photo of ${name}`}
            >
                {/* Proxied like every other <img> in this package: an operator-pasted
                    http:// URL is otherwise mixed-content-blocked on an HTTPS property
                    page, and a full-resolution original loads into a 4:3 thumbnail.
                    getProxiedImageUrlOnce (not getProxiedImageUrl) — dashboard#1519:
                    parseImageFieldValue can hand back a value that is ALREADY an hrimgs.co
                    URL, and proxying that again renders the placeholder. */}
                <img className="homerunner-spaces-photo" src={getProxiedImageUrlOnce(images[0], 640, 480)} alt="" loading="lazy" decoding="async" />
            </button>
        )
    }

    const label = (i: number) => captions[i] || `${name} (${i + 1} of ${total})`
    const mounted = interactive ? seen : [0]
    return (
        <div
            className="homerunner-spaces-slides"
            data-transition={transition}
            role="group"
            aria-roledescription="slideshow"
            aria-label={`${name} photos`}
            onKeyDown={(e) => {
                if (!interactive) return
                if (e.key === 'ArrowLeft') { e.preventDefault(); go(current - 1) }
                else if (e.key === 'ArrowRight') { e.preventDefault(); go(current + 1) }
            }}
        >
            {mounted.slice().sort((a, b) => a - b).map((i) => {
                const state = i === current ? 'is-active' : i < current ? 'is-before' : 'is-after'
                return (
                    <button
                        type="button"
                        key={i}
                        className={`homerunner-spaces-photo-trigger homerunner-spaces-slide ${state}`}
                        onClick={(e) => onOpen(i, e.currentTarget)}
                        aria-label={`View photo: ${label(i)}`}
                        aria-hidden={i === current ? undefined : true}
                        tabIndex={i === current ? undefined : -1}
                    >
                        <img className="homerunner-spaces-photo" src={getProxiedImageUrlOnce(images[i], 640, 480)} alt="" loading={i === 0 ? 'lazy' : 'eager'} decoding="async" />
                    </button>
                )
            })}
            {interactive && (
                <>
                    <button type="button" className="homerunner-spaces-photo-nav homerunner-spaces-photo-prev" onClick={() => go(current - 1)} aria-label="Previous photo">
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4L7 12l8 8" /></svg>
                    </button>
                    <button type="button" className="homerunner-spaces-photo-nav homerunner-spaces-photo-next" onClick={() => go(current + 1)} aria-label="Next photo">
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 4l8 8-8 8" /></svg>
                    </button>
                    <div className="homerunner-spaces-photo-dots">
                        {images.map((_, i) => (
                            <button
                                type="button"
                                key={i}
                                className="homerunner-spaces-photo-dot"
                                onClick={() => go(i)}
                                aria-label={`Photo ${i + 1} of ${total}`}
                                aria-current={i === current ? 'true' : undefined}
                            />
                        ))}
                    </div>
                    <span className="homerunner-spaces-photo-count" aria-live="polite">{current + 1} / {total}</span>
                </>
            )}
        </div>
    )
}

function SpacesContent() {
    const { options, resolvedTheme, widgetId } = useWidget<SpacesWidgetConfig>()
    const { data: spaces, isError, error } = useFetchSpaces(options.filter)

    const colors = resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors

    // Certainty-gate (#998, same philosophy as #996 descriptions): PMS-default
    // placeholder spaces ("Default bedroom", no beds) never render to guests.
    // If suppression empties a non-empty list, the widget renders nothing at
    // all — the no-items text is reserved for a genuinely empty API result.
    const visibleSpaces = useMemo(() => (spaces ?? []).filter((s) => !isPlaceholderSpace(s)), [spaces])
    const hasContent = visibleSpaces.length > 0

    // dashboard#1420 PR B — bedroom showcase photo slots (toolkit
    // bedroom-showcase parity). Only fetched when the setting is on and a
    // property is actually resolved (standalone embed with no property
    // filter must not fetch); field-gated beyond that — see
    // resolveBedroomMedia.
    const canFetchMedia = options.showBedroomMedia && !!options.filter?.property && !!options.filter?.feed_id
    const { data: customFields } = usePropertyCustomFields(
        { feed_id: options.filter?.feed_id ?? 0, platform: options.filter?.platform ?? null, property: options.filter?.property ?? '' },
        { enabled: canFetchMedia },
    )
    const indexBySpace = useMemo(() => bedroomIndexes(visibleSpaces), [visibleSpaces])

    // Room photos from the PMS room map (toolkit bedroom-showcase 9/20; on
    // by default like the toolkit) and caption fill (dashboard#1707, toolkit
    // 3.246.0/3.247.0; off by default). The property photos come ONLY from
    // the key the Gallery widget owns, through a passive observer that never
    // fetches (see ./gallery-photos.ts), so neither adds a request on any
    // page. Best-effort by construction: the key holds the pages a Gallery on
    // the page has loaded. Since #1810 that is every page up to
    // GALLERY_MAX_PAGES (10 x 50), landing one at a time after mount, so the
    // fill re-derives as they arrive instead of seeing page 1 only.
    const captionFill = canFetchMedia && options.matchBedroomCaptions
    const roomFill = canFetchMedia && options.matchBedroomRooms
    const sharedFill = captionFill || roomFill
    const galleryFilter = useMemo(
        () => galleryFilterFor(options.filter),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [options.filter?.feed_id, options.filter?.property, options.filter?.platform],
    )
    const listingPhotos = useSharedGalleryPhotos(sharedFill ? galleryFilter : null, sharedFill)

    const mediaBySpace = useMemo(() => {
        const map = new Map<number, RoomMedia>()
        if (!canFetchMedia) return map
        const fieldMedia = new Map<number, BedroomMedia>()
        for (const [spaceId, index] of indexBySpace) fieldMedia.set(spaceId, resolveBedroomMedia(index, customFields))

        const bedroomIds = [...indexBySpace.keys()]
        const bedrooms = bedroomIds.map((spaceId) => visibleSpaces.find((s) => s.id === spaceId))

        // The room map goes first: it is the operator's own assignment. Every
        // bedroom takes part, and every photo of a matched room leaves the
        // pool whether or not that bedroom keeps its own field photos, so
        // caption matching cannot hand one bedroom's tagged photo to another.
        const byRoom = roomFill && listingPhotos.length > 0
            ? assignRoomPhotos(listingPhotos, bedrooms.map((space) => ({ name: space?.name, number: space?.number })))
            : new Map<number, { uid: string; photos: { url: string; caption: string }[] }>()
        const claimed = new Set<string>()
        for (const room of byRoom.values()) for (const photo of room.photos) claimed.add(photo.url)
        const leftover = claimed.size > 0 ? listingPhotos.filter((photo) => !claimed.has(photo.url)) : listingPhotos

        // Every bedroom takes part in the caption matching, so a photo
        // captioned for a room that keeps its own field photos is not handed
        // to a neighbour; only rooms with an empty photo field take photos.
        const byPosition = captionFill && leftover.length > 0
            ? assignCaptionPhotos(
                leftover,
                bedrooms.map((space, position) => (space?.name ?? '').trim() || `Bedroom ${indexBySpace.get(bedroomIds[position])}`),
            )
            : new Map<number, { url: string; caption: string }[]>()

        bedroomIds.forEach((spaceId, position) => {
            const field = fieldMedia.get(spaceId) ?? { images: [], details: '' }
            const filled = field.images.length === 0 ? (byRoom.get(position)?.photos ?? byPosition.get(position)) : undefined
            const images = filled ? filled.map((p) => p.url) : field.images
            const captions = filled ? filled.map((p) => p.caption) : field.images.map(() => '')
            const roomUid = byRoom.get(position)?.uid
            if (images.length > 0 || field.details !== '') map.set(spaceId, { images, captions, details: field.details, roomUid })
        })
        return map
    }, [canFetchMedia, indexBySpace, customFields, captionFill, roomFill, listingPhotos, visibleSpaces])

    // Lightbox: one shared overlay for the whole grid, keyed by space id so
    // prev/next stays within that room's own photo set. Presence, the focus
    // round-trip and the focus trap are the gallery lightbox's own hooks
    // (#1518): useModalPresence captures the trigger when the overlay opens
    // and hands focus back to it on close, useFocusTrap keeps Tab inside the
    // overlay while it is open, and the close button takes focus on mount via
    // autoFocus (on mount only, so stepping photos never yanks focus off the
    // Previous/Next button the guest just activated). Mounting runs one
    // render behind `isLightboxOpen`; that is what lets the presence hook
    // read the trigger as document.activeElement before autoFocus lands.
    const [lightbox, setLightbox] = useState<{ spaceId: number; index: number } | null>(null)
    const isLightboxOpen = lightbox !== null
    const lightboxMedia = lightbox ? mediaBySpace.get(lightbox.spaceId) : undefined
    const lightboxImages = lightboxMedia?.images ?? []
    // This overlay has no exit animation, so exitMs is 0 and `visible` only
    // drives the entrance fade.
    const { mounted: lightboxMounted, visible: lightboxVisible } = useModalPresence(isLightboxOpen, 0)
    const lightboxRef = useRef<HTMLDivElement | null>(null)
    useFocusTrap(lightboxRef, isLightboxOpen)
    const openLightbox = (spaceId: number, index: number, trigger: HTMLElement) => {
        // Safari does not focus a button on mouse click. Focusing the trigger
        // here makes it what the presence hook captures, in every browser, so
        // focus comes back to the photo the guest opened.
        trigger.focus()
        // Tap = tour (toolkit#798 / 3.275.0): a Gallery widget on the page
        // for this property takes the tap and opens its "Show all photos"
        // view at this photo, or at this room. Anything short of a clean
        // "taken" (no gallery, another property, unknown room) and the viewer
        // below opens as before.
        if (options.bedroomPhotoTap === 'tour') {
            const media = mediaBySpace.get(spaceId)
            const space = visibleSpaces.find((s) => s.id === spaceId)
            const taken = requestGalleryOpenAt({
                feedId: options.filter?.feed_id,
                property: options.filter?.property ?? '',
                url: media?.images[index],
                roomUid: media?.roomUid,
                roomName: (space?.name ?? '').trim() || undefined,
            })
            if (taken) return
        }
        setLightbox({ spaceId, index })
    }
    const closeLightbox = () => setLightbox(null)
    // Functional update: the keydown listener below is registered once per
    // OPEN, so it must not close over the current index.
    const stepLightbox = (delta: number) => {
        setLightbox((current) => {
            if (!current) return current
            const count = mediaBySpace.get(current.spaceId)?.images.length ?? 0
            if (count === 0) return current
            return { spaceId: current.spaceId, index: (current.index + delta + count) % count }
        })
    }

    useEffect(() => {
        if (!isLightboxOpen) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            // The listener is on `window`, in the HOST page's document: without
            // this, arrow keys stepped the photo AND scrolled the page behind
            // the overlay.
            e.preventDefault()
            if (e.key === 'Escape') closeLightbox()
            else stepLightbox(e.key === 'ArrowLeft' ? -1 : 1)
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLightboxOpen])

    // Dark accent for the auto FIRST-PAINT path (#900), read from the DARK color
    // set directly (not gated on `resolvedTheme`) so it ships on every render —
    // auto first paint can't wait for the runtime to flip `resolvedTheme`. The
    // theme-gated light emission below stays byte-identical (append-only).
    const darkAccent = options.darkModeColors.accent

    const cssString = `
        .homerunner-spaces-container *:not([class^=icon-]) {
            font-family: '${options.font.family}' !important;
        }
        ${hostFontSizeCss(widgetId, options.font.size)}
        ${colors.accent ? `.homerunner-spaces-container .text-accent { color: ${colors.accent} !important; }` : ''}
        ${darkAccent ? `
        /* Dark accent under the runtime marker (explicit dark + post-hydration
           auto), and under the server-stamped auto host on a dark OS for a
           first-paint override with no JS/cookie. The base rule above always
           emits the theme-gated (light) accent, so light output is unchanged.
           BOTH conditions live INSIDE :host(...) — a ':not' placed after the
           parens would silently never match the shadow host. */
        :host([data-hr-theme="dark"]) .homerunner-spaces-container .text-accent {
            color: ${darkAccent} !important;
        }
        @media (prefers-color-scheme: dark) {
            :host([data-hr-scheme="auto"]:not([data-hr-theme])) .homerunner-spaces-container .text-accent {
                color: ${darkAccent} !important;
            }
        }
        ` : ''}
        ${options.customCss ?? ''}
    `

    if (isError) {
        return <WidgetDataError error={error} />
    }

    return (
        <div
            className={`homerunner-spaces-container w-full ${resolvedTheme === 'dark' ? 'dark' : ''}`}
            style={{
                ...resolveWidgetSpacingStyle(options),
                '--spaces-columns-sm': options.columns.sm,
                '--spaces-columns-md': options.columns.md,
                '--spaces-columns-lg': options.columns.lg,
            } as React.CSSProperties}
            suppressHydrationWarning
        >
            <WebFont icoFont={true} />
            <Stylesheet css={cssString} />

            {hasContent && <WidgetSection show={options.section?.show} title={options.section?.title} forceHeadingInMarkup />}

            {hasContent ? (
                <div className="homerunner-spaces-grid">
                    {visibleSpaces.map((space) => {
                        const media = mediaBySpace.get(space.id)
                        return (
                            <div className="homerunner-spaces-card" key={space.id}>
                                {media && media.images.length > 0 && (
                                    <RoomPhotos
                                        name={space.name || 'this room'}
                                        media={media}
                                        transition={options.bedroomSlideshowTransition}
                                        onOpen={(index, trigger) => openLightbox(space.id, index, trigger)}
                                    />
                                )}
                                <h3 className="homerunner-spaces-card-title">
                                    {options.showRoomIcon && (
                                        <span className="homerunner-spaces-icon icomoon-icon text-accent"><i className={options.roomIcon}></i></span>
                                    )}
                                    <span>{space.name || `Room ${space.number ?? ''}`.trim()}</span>
                                    {options.showTotalBeds && space.total_bed_count > 0 && (
                                        <span className="homerunner-spaces-total">({space.total_bed_count})</span>
                                    )}
                                </h3>
                                {space.beds.length > 0 && (
                                    <ul className="homerunner-spaces-bed-list">
                                        {space.beds.map((bed, i) => (
                                            <li className="homerunner-spaces-bed" key={`${space.id}-${bed.type}-${i}`}>
                                                {options.showBedIcon && (
                                                    <span className="homerunner-spaces-bed-icon icomoon-icon text-accent"><i className={options.bedIcon}></i></span>
                                                )}
                                                <span className="homerunner-spaces-bed-count">{bed.count}</span>
                                                <span className="homerunner-spaces-bed-name">{bed.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {media && media.details !== '' && (
                                    // Sanitized server-side (common/bedroom-showcase.ts
                                    // sanitizeBedroomDetails): a fixed allowlist of
                                    // structural tags, every attribute stripped except a
                                    // scheme-validated href on <a> — see that file's
                                    // inline notes for why this is safe to inject.
                                    <div
                                        className="homerunner-spaces-details"
                                        dangerouslySetInnerHTML={{ __html: media.details }}
                                    />
                                )}
                            </div>
                        )
                    })}
                </div>
            ) : (
                spaces && spaces.length === 0 && <p className="homerunner-spaces-empty">{options.noItemsText}</p>
            )}

            {lightboxMounted && lightbox && lightboxImages.length > 0 && (
                <div
                    ref={lightboxRef}
                    className={`homerunner-spaces-lightbox${lightboxVisible ? ' is-open' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Room photo"
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        autoFocus
                        className="homerunner-spaces-lightbox-close"
                        onClick={closeLightbox}
                        aria-label="Close"
                    >
                        &#10005;
                    </button>
                    {lightboxImages.length > 1 && (
                        <button
                            type="button"
                            className="homerunner-spaces-lightbox-nav homerunner-spaces-lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); stepLightbox(-1) }}
                            aria-label="Previous photo"
                        >
                            &#10094;
                        </button>
                    )}
                    <img
                        className="homerunner-spaces-lightbox-img"
                        // getProxiedImageUrlOnce — see the room-card <img> above (dashboard#1519).
                        src={getProxiedImageUrlOnce(lightboxImages[lightbox.index], 1600, 1200)}
                        alt={lightboxMedia?.captions[lightbox.index] ?? ''}
                        onClick={(e) => e.stopPropagation()}
                    />
                    {lightboxImages.length > 1 && (
                        <button
                            type="button"
                            className="homerunner-spaces-lightbox-nav homerunner-spaces-lightbox-next"
                            onClick={(e) => { e.stopPropagation(); stepLightbox(1) }}
                            aria-label="Next photo"
                        >
                            &#10095;
                        </button>
                    )}
                    {lightboxImages.length > 1 && (
                        <span className="homerunner-spaces-lightbox-count">
                            {lightbox.index + 1} / {lightboxImages.length}
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

export default function SpacesWidget({ options, feedId, widgetId, resolvedTheme, data }: WidgetProps<SpacesWidgetConfig>) {
    const parsedOptions = useMemo(() => parseWidgetConfig(spacesWidgetConfig, options), [options])
    return (
        <WidgetProvider options={parsedOptions} feedId={feedId} widgetId={widgetId} resolvedTheme={resolvedTheme} widgetType="spaces" data={data ?? {}}>
            <SpacesContent />
        </WidgetProvider>
    )
}
